import { Injectable, Injector } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Http } from '@angular/http';
import { ApiCollection } from './collection';
import { TranslateService } from '@ngx-translate/core';
import 'rxjs-compat/add/operator/map';
import 'rxjs-compat/add/operator/finally';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { ConfigService } from '../config/config.service';
import { AuthenticationService } from '../authentication/services/authentication.service';
import { RouteService } from '../routing/services/route.service';
import { MultiTenantService } from '../authentication/services/multi-tenant.service';
import { ToastService } from '../toast/services/toast.service';
import * as jqueryProxy from 'jquery';
import moment from 'moment-es6';
import { ApiServiceInterface } from './api-service.interface';
import { Subject } from 'rxjs/Subject';
import { Observer } from 'rxjs/Observer';
import { clone } from '../support/helpers/clone.function';
import { Router } from '@angular/router';
import { Model } from './model';
import * as pluralize from 'pluralize';

const $: JQueryStatic = (<any>jqueryProxy).default || jqueryProxy;

const json = (response) => {
    return typeof response.json === 'function' ? response.json() :
        JSON.parse(response);
};

const mapError = (error) => {
    error.body = json(error._body);
    delete error._body;
    return error;
};

interface Attributes {
    [key: string]: any
}

@Injectable({
    providedIn: 'root',
})
export abstract class ApiService<T = any> implements ApiServiceInterface {

    public static discriminator: 'ApiService' = 'ApiService';

    public restEndpoint: string;

    public model;

    public collection = ApiCollection;

    public defaultParams = {};

    public entityName = null;

    /**
     * When the model is not entity buildable you can provide
     * the permission name.
     *
     * @type {any}
     */
    public permissionName = null;

    /**
     * Data will be cached if set to true
     *
     * @type {boolean}
     */
    protected cache = false;

    /**
     * Reactive cache enabled?
     * Reactive cache updates automatically when a pusher event is triggered from the backend
     * TODO: implement
     *
     * @type {boolean}
     */
    protected reactiveCache = false;

    /**
     * Persist the cache local so that it is persisted on application restart
     *
     * @type {boolean}
     */
    protected localCache = false;

    /**
     * Cache expire time in seconds
     *
     * @type {any}
     */
    protected cacheExpire = null;

    /**
     * Last timestamp cache was updated
     *
     * @type null|{moment.Moment}
     */
    protected cacheUpdate = null;

    /**
     * Contains cached data from the server
     */
    protected cachedData: ApiCollection;

    /**
     * Always returns the last known cached value
     *
     * @type {ReplaySubject}
     */
    protected cacheSubject: ReplaySubject<ApiCollection> = new ReplaySubject(1);

    /**
     * Indicates whether cache is being generated
     *
     * @type {boolean}
     */
    protected busyCaching = false;

    /**
     * The Http instance.
     */
    protected http: Http;

    /**
     * The AuthenticationService instance.
     */
    protected authService: AuthenticationService;

    /**
     * The RouteService instance.
     */
    protected routeService: RouteService;

    /**
     * The ToastService instance.
     */
    protected toastService: ToastService;

    /**
     * The TranslateService instance.
     */
    protected translateService: TranslateService;

    protected tenantService: MultiTenantService;

    protected router: Router;

    protected config: ConfigService;

    protected with: Array<string>;

    /**
     * Construct the service
     *
     * @param injector
     */
    public constructor(public injector: Injector) {
        this.http             = injector.get(Http);
        this.config           = injector.get(ConfigService);
        this.authService      = injector.get(AuthenticationService);
        this.tenantService    = injector.get(MultiTenantService);
        this.routeService     = injector.get(RouteService);
        this.toastService     = injector.get(ToastService);
        this.translateService = injector.get(TranslateService);
        this.translateService = injector.get(TranslateService);
        this.router           = injector.get(Router);

        // Set authentication headers.
        // Set basic http headers.
        this.http['_defaultOptions'].headers.set('Content-Type', 'application/json');
        this.http['_defaultOptions'].headers.set('Accept', 'application/json');

        // set Multi Tenant Header if enabled
        if (this.tenantService.isEnabled()) {
            this.tenantService.onScopeChange().subscribe((value) => {
                if (value) {
                    this.http['_defaultOptions'].headers.set('X-TenantScope', this.tenantService.getCurrentScope());
                } else {
                    this.http['_defaultOptions'].headers.delete('X-TenantScope');
                }
            });
        }

        this.authService.tokenTrigger.subscribe(token => {
            if (token) {
                this.http['_defaultOptions'].headers.set('Authorization', 'Bearer ' + token);
            } else {
                this.http['_defaultOptions'].headers.delete('Authorization');
            }
        });
    }

    protected boot(model): void {
    }

    public createModel(attributes: Attributes): T {
        const model = this.model.make(attributes, this);

        // Boot the model's attributes after the model's creation to
        // prevent problems caused by object inheritance.
        model.bootAttributes();

        if (this.model.retrieved) {
            this.model.retrieved(model, this.injector);
        }

        this.bootModel(model);

        return model;
    }

    protected createModelArray(attributes: Array<Attributes>): Array<T> {
        return Array.from(attributes).map((a) => {
            return this.createModel(a);
        });
    }

    /**
     * Transform a given array to an api collection
     *
     * @param {Array<Attributes>} data
     *
     * @returns {ApiCollection}
     */
    public createModelCollection(data: Array<Attributes>): ApiCollection {
        return new ApiCollection(data, this.model, this);
    }

    /**
     * Boot model.
     *
     * @param model
     */
    protected bootModel(model) {
        if (this.model.booting) {
            this.model.booting(model, this.injector);
        }

        this.boot(model);

        if (this.model.booted) {
            this.model.booted(model, this.injector);
        }
    }

    /**
     * Set params.
     *
     * @param params
     */
    public params(params?: { [key: string]: string | number | boolean }): void | Attributes {
        if (params) {
            this.defaultParams = Object.assign(this.defaultParams, params);
        } else {
            return this.defaultParams;
        }
    }

    public addParam(param: { [key: string]: string | number | boolean }) {
        this.params(param);
    }

    public removeParam(param: string) {
        delete this.defaultParams[param];
    }

    /**
     * Set included relations.
     *
     * @param relations
     */
    public include(relations: Array<string>) {
        this.with = this.with ? this.with.concat(relations) : relations;

        return this;
    }

    /**
     * Get a collection of the entity.
     *
     * @param returnUrl
     * @param params
     * @returns {any}
     */
    public all(returnUrl = false, params = {}): any {
        // Set the endpoint url.
        const url = this.getEndpointUrl();

        // Get the requested data or the request endpoint url.
        let request = this.request('get', url, true, null, params, returnUrl);

        // Return the request endpoint url.
        if (returnUrl) {
            return request;
        }

        if (this.cacheEnabled()) {
            // Return the requested data.
            request          = this.request('get', url, true, null, params, returnUrl, false);
            const observable = <Observable<any>>request;
            return this.processCacheRequest(observable);
        } else {
            return request;
        }
    }

    /**
     * Get all with the specified relations at once
     *
     * @param returnUrl
     * @param relations
     * @param params
     * @returns {string|any}
     */
    public allWith(returnUrl = false, relations: Array<string> = [], params = {}) {
        // If there are relations that need to be retrieved we'll add the relations to the include
        // parameter.
        if (relations.length) {
            params['include'] = relations.join(',');
        }

        // Return the observable containing the requested data.
        return this.all(returnUrl, params);
    }

    /**
     * Perform a backend pagination call
     *
     * @param {number} page
     * @param {number} per_page
     * @returns {any}
     */
    public paginate(page = 1, per_page = 10, params = {}) {

        params = Object.assign({
            limit: per_page,
            offset: (page - 1) * per_page
        }, params);

        return this.all(false, params);
    }

    /**
     * Get an item by id of the entity
     *
     * @param id
     * @param returnUrl
     * @param params
     * @returns {any}
     */
    public one(id: number, returnUrl = false, params = {}): any {
        // Set the endpoint url.
        const url = id !== null ? this.getEndpointUrl() + '/' + id : this.getEndpointUrl();

        // Get the requested data or the request endpoint url.
        const request = this.request('get', url, false, {}, params, returnUrl);

        // Return the request endpoint url or return the observable containing
        // the requested data.
        if (returnUrl || !this.cachedData) {
            return request;
        }

        // Return the observable containing the requested data.
        return new Observable(observer => {
            const result = this.cachedData.find('id', id);

            observer.next(result);
        });
    }

    /**
     * Get an entity instance with the specified relations
     *
     * @param id
     * @param relations
     * @param returnUrl
     * @param params
     * @returns {any|any}
     */
    public oneWith(id: number, relations: Array<string>, returnUrl = false, params = {}) {
        // If there are relations that need to be retrieved we'll add the relations to the include
        // parameter.
        if (relations.length) {
            params['include'] = relations.join(',');
        }

        // Return the requested data.
        return this.one(id, returnUrl, params);
    }

    /**
     * Post new object to resource
     *
     * @param data
     * @param params
     * @returns {Observable}
     */
    public post(data: Object, params: Object = {}): Observable<any> {
        return this.request('post', this.getEndpointUrl(), false, data, params);
    }

    /**
     * Put existing object to resource
     *
     * @param identifier
     * @param data
     * @param params
     * @returns {Observable}
     */
    public put(identifier, data: Object, params: Object = {}): Observable<any> {
        // check if id is set and > 0
        if (!identifier || identifier <= 0) {
            throw new Error('Cannot put a data object without an identifier');
        }

        return this.request('put', this.getEndpointUrl() + '/' + identifier, false, data, params);
    }

    /**
     * Send a patch request.
     *
     * @param id
     * @param data
     * @param params
     * @returns {Observable}
     */
    public patch(id, data: Object = {}, params: Object = {}): Observable<any> {
        return this.request('patch', this.getEndpointUrl(), false, data, params);
    }

    /**
     * Delete resource
     *
     * @param id
     * @param params
     * @returns {Observable}
     */
    public delete(id: number, params: Object = {}): Observable<any> {
        return this.request('delete', this.getEndpointUrl() + '/' + id, false, {}, params);
    }

    /**
     * Return a new instance.
     *
     * @param attributes
     * @returns {Model}
     */
    public make(attributes: Attributes = {}): T {
        return this.createModel(attributes);
    }

    /**
     * Get the base endpoint url.
     *
     * @returns {string}
     */
    public getBaseEndpoint() {
        const apiUrl = this.config.get('api_url');
        return apiUrl.substr(0, -1) === '/' ? apiUrl : apiUrl + '/';
    }

    /**
     * Get the url of the endpoint
     *
     * @returns {string}
     */
    public getEndpointUrl() {
        return this.getBaseEndpoint() + this.restEndpoint;
    }

    public clear(): this {
        this.defaultParams = {};

        return this;
    }

    /**
     * Create a new request
     *
     * @param method
     * @param url
     * @param isApiCollection
     * @param data
     * @param params
     * @param returnUrl
     * @param handleResponse
     * @returns {Observable}
     */
    public request(method: string, url: string, isApiCollection = false, data = {}, params = {}, returnUrl = false, handleResponse = true): any {
        // Clone object to remove references
        const defaultParams = clone(this.defaultParams);

        // Merge given parameters with default parameters
        params = Object.assign(defaultParams, params);

        // Add default eager loaded relations
        if (method !== 'delete') {
            if (this.with && this.with.length) {
                params['include'] = this.with.join(',');
            }
        }

        url = this.buildEndpointUrl(url, params);

        if (returnUrl) {
            return this.addParamsToUrl(url, params);
        }

        return new Observable(observer => {
            const request = new ApiRequest(method, url, isApiCollection, data, params);

            const options = {
                method: method,
                body: data,
                params: params
            };

            this.http
                .request(url, options)
                .subscribe((response) => {
                    response = json(response);

                    handleResponse
                        ? this.handleHttpResponse(observer, response, isApiCollection, request)
                        : observer.next(response);
                }, (error) => {
                    this.handleHttpError(observer, mapError(error), request);
                }, () => {
                    this.handleHttpCompletion(observer);
                });

            // this.router.events.filter((event) => {
            //     return event instanceof NavigationEnd;
            // }).subscribe(() => {
            //     subscription.unsubscribe();
            // });
        });
    }

    public allCached() {
        return this.cachedData;
    }

    protected addParamsToUrl(url: string, params) {
        return url + (Object.keys(params).length ? '?' + $.param(params) : '');
    }

    public custom(method: string, url: string, data: Attributes = {} = {}, params: { [key: string]: string | number } = {}, isCollection = false, isModel = false) {
        // exclude params that are part of endpoint
        params = Object.assign(Object.assign({}, this.defaultParams), params);

        // Add default eager loaded relations
        if (method !== 'delete') {
            if (this.with && this.with.length) {
                params['include'] = this.with.join(',');
            }
        }

        url = this.buildEndpointUrl(url, params);

        return new Observable((observer) => {
            const request = new ApiRequest(method, url);

            this.http.request(url, {
                method: method,
                body: data,
                params: params
            })
                .map(response => {
                    return response.json();
                })
                .subscribe(
                    (response) => {
                        if (isModel || isCollection) {
                            return this.handleHttpResponse(observer, response, isCollection, request);
                        } else {
                            return this.handleCustomHttpResponse(observer, response);
                        }
                    },
                    (error) => {
                        this.handleHttpError(observer, mapError(error), request);
                    }, () => {
                        this.handleHttpCompletion(observer)
                    }
                );
        });
    }

    /**
     * Handle successful response
     *
     * @param observer
     * @param response
     * @param isApiCollection
     * @param request
     * @param endpointParams
     */
    protected handleHttpResponse(observer, response, isCollection: boolean, request): ApiCollection | T {
        if (request.method === 'delete') {
            observer.next();
            return;
        }

        const data = response.data;

        const value = isCollection
            ? this.createModelCollection(data)
            : this.createModel(data);

        observer.next(value)
    }

    /**
     * Handle success response on custom requests
     *
     * @param observer
     * @param response
     * @returns {boolean}
     */
    public handleCustomHttpResponse(observer, response) {
        observer.next(response.data);
        return true;
    }

    /**
     * Handle the errors
     *
     * @param observer
     * @param error
     * @param request
     */
    protected handleHttpError(observer: Observer<any>, error, request: ApiRequest) {
        switch (error.status) {
            case 401:
                if (error.body.message === 'Token has expired') {
                    return this.refreshTokenAndRepeatRequest(request)
                        .subscribe((response) => {
                            console.log('Token refreshed and repeated request');

                            observer.next(response);
                        });
                }

                this.authService.logout();
                break;

            default:
                observer.error({
                    status: error.status,
                    message: error.body.message,
                    errors: error.body.errors,
                });
                break;
        }
    }

    /**
     * Handle completed Http request
     *
     * @param observer
     */
    protected handleHttpCompletion(observer) {
        observer.complete();
    }

    /**
     * Refresh token and repeat the request
     *
     * @param request
     * @returns {Observable<R>}
     */
    protected refreshTokenAndRepeatRequest(request: ApiRequest) {
        this.toastService.info('', this.translateService.instant('We are renewing your session.'));

        return this.authService.refreshToken().switchMap((refreshAccesstokenResponse) => {
            console.log('Refresh token');

            // update Authorization header for this specfic request (restangular will be updated automatically)
            this.http['_defaultOptions'].headers.set('Authorization', 'Bearer ' + refreshAccesstokenResponse);

            return this.request(request.method, request.url, request.isApiCollection, request.data, request.params);
        });
    }

    /**
     * Prepare the url and replace variables in endpoint string
     *
     * @param url
     * @param params
     * @returns {string}
     */
    private buildEndpointUrl(url: string, params: { [key: string]: string | number } = {}): string {
        let matches;

        if (matches = url.match(/([^{]*?)\w(?=\})/g)) {
            matches.forEach((match: string) => {
                if (params[match]) {
                    url = url.replace('{' + match + '}', <string>params[match]);

                    delete params[match];
                }
            });
        }

        return url;
    }

    public disableCache() {
        this.cache = false;
    }

    public enableCache() {
        this.cache = false;
    }

    public resetCache() {
        this.removeLocalCachedData();
    }

    /**
     * @returns {boolean}
     */
    protected cacheEnabled() {
        return this.cache === true;
    }

    /**
     * Checks whether we should retrieve data from cache based
     * on expire time, already cached data and cache setting
     *
     * @returns {boolean|number}
     */
    protected shouldRequestFromCache() {
        return this.busyCaching === true || this.cachedData !== undefined || !this.cacheExpired();
    }

    /**
     * Returns whether the cache is expired or even completely disabled
     *
     * @returns {boolean|number}
     */
    protected cacheExpired() {
        return this.cacheExpire !== null && (this.cacheUpdate === null || moment().diff(this.cacheUpdate, 'seconds') > this.cacheExpire);
    }

    /**
     * Subscribe the backend request and cache the data
     *
     * @param requestObservable
     */
    protected refreshCache(requestObservable: Observable<any>) {

        this.busyCaching = true;

        // do we have localCaching enabled and does it exist ?
        if (this.localCache && this.hasLocalCachedData() && !this.localCacheExpired()) {
            // yes, let's retrieve the cache from local storage
            const collection = this.getLocalCachedData();

            const observable = new Subject();
            observable.subscribe((result: ApiCollection) => {

                this.cachedData = result;
                this.cacheSubject.next(result);
                this.busyCaching = false;

            });

            this.handleHttpResponse(observable, {data: collection}, true, {});

        } else {
            // nope, we don't so request from the api
            requestObservable.subscribe(collection => {
                this.cacheUpdate = moment();

                const observable = new Subject();
                observable.subscribe((result: ApiCollection) => {

                    this.cachedData = result;

                    if (this.localCache) {
                        this.setLocalCachedData(collection.data);
                    }

                    this.cacheSubject.next(result);
                    this.busyCaching = false;

                });

                this.handleHttpResponse(observable, collection, true, {});
            });

        }

    }

    protected hasLocalCachedData() {
        return localStorage.getItem(this.restEndpoint + '-all-cache') !== null;
    }

    protected getLocalCachedData() {
        return JSON.parse(localStorage.getItem(this.restEndpoint + '-all-cache'));
    }

    protected setLocalCachedData(data) {
        localStorage.setItem(this.restEndpoint + '-all-cache-expiry', moment().add(this.cacheExpire, 'seconds').format('YYYY-MM-DD HH:mm:ss'));
        return localStorage.setItem(this.restEndpoint + '-all-cache', JSON.stringify(data));
    }

    protected removeLocalCachedData() {
        localStorage.removeItem(this.restEndpoint + '-all-cache-expiry');
        localStorage.removeItem(this.restEndpoint + '-all-cache');
        this.cachedData = undefined;
    }

    protected localCacheExpired() {
        return moment(localStorage.getItem(this.restEndpoint + '-all-cache-expiry')).diff(moment(), 'seconds') <= 0;
    }

    /**
     * Returns http observable or cached data depending on cache settings
     *
     * @param requestObservable
     * @returns {any}
     */
    protected processCacheRequest(requestObservable: Observable<any>) {

        // when cache is enabled, we will return a replay subject
        // and check whether we have to refresh the cache
        if (!this.shouldRequestFromCache()) {
            this.refreshCache(requestObservable);
        }

        return this.cacheSubject;
    }

    /**
     * Get the morph type for this service
     *
     * @returns {string|boolean}
     */
    public getMorphType() {
        /**
         * If the service has an entity name, base it on that
         */
        if (this.entityName) {
            return pluralize(this.entityName).replace('_', '-').toLowerCase();
        }
        /**
         * If the service has a rest endpoint, base it on that
         */
        if (this.restEndpoint) {
            return this.restEndpoint;
        }

        /**
         * If the service has a model, base it on that
         */
        if (this.model) {
            const instance = new this.model;
            // Remove 'Model' => make kebab_case => pluralize => lowercase
            return pluralize(instance.constructor.name.replace('Model', '').replace(/([a-z0-9])([A-Z])/g, '$1-$2')).toLowerCase();
        }

        return false;
    }
}

export class ApiRequest {

    /**
     * Construct and set request properties
     *
     * @param method
     * @param url
     * @param isApiCollection
     * @param data
     * @param params
     */
    constructor(public method, public url, public isApiCollection = false, public data = {}, public params = {}) {
    }

}

export interface ApiInterface {

    restEndpoint: string;
    model: Function;
    collection?: Object;
    entityName?: string;

}

export class ApiMockService {
    public all() {
        return new Observable(observer => {
            observer.complete();
        });
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/api/api.service.ts